import React, { useState, useEffect } from "react";
import { api, API_BASE_URL } from "../../config/api";
import { useMyContext } from "../../context/MyContext";
import { toast } from "react-toastify";

const currencyOptions = [
  { name: "USD", symbol: "$" },
  { name: "EUR", symbol: "€" },
  { name: "GBP", symbol: "£" },
];

const CurrencyEditModal = ({ showModal, setShowModal, currency, onUpdate }) => {
  const { token, user } = useMyContext();

  const [currencyData, setCurrencyData] = useState({
    name: "",
    symbol: "",
    exchangeRates: {
      USD: "",
      EUR: "",
      GBP: ""
    }
  });
  const [loading, setLoading] = useState(false);

  // Auto-fill form when currency data changes
  useEffect(() => {
    if (currency) {
      setCurrencyData({
        name: currency.name || "",
        symbol: currency.symbol || "",
        exchangeRates: {
          USD: currency.exchangeRates?.USD || "",
          EUR: currency.exchangeRates?.EUR || "",
          GBP: currency.exchangeRates?.GBP || ""
        }
      });
    }
  }, [currency]);

  // Handle exchange rate input change
  const handleExchangeRateChange = (targetCurrency, value) => {
    setCurrencyData(prev => ({
      ...prev,
      exchangeRates: {
        ...prev.exchangeRates,
        [targetCurrency]: value
      }
    }));
  };

  // Handle update request
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate that at least one exchange rate is provided (other than the base currency)
      const nonBaseCurrencies = Object.keys(currencyData.exchangeRates).filter(
        curr => curr !== currencyData.name
      );

      const hasValidRate = nonBaseCurrencies.some(
        curr => currencyData.exchangeRates[curr] &&
          parseFloat(currencyData.exchangeRates[curr]) > 0
      );

      if (!hasValidRate) {
        toast.error("Please provide at least one exchange rate.");
        setLoading(false);
        return;
      }

      // Convert string rates to numbers, keeping null for empty values
      const processedExchangeRates = {};
      Object.keys(currencyData.exchangeRates).forEach(curr => {
        const rate = currencyData.exchangeRates[curr];
        if (curr === currencyData.name) {
          processedExchangeRates[curr] = 1; // Base currency is always 1
        } else if (rate && !isNaN(parseFloat(rate))) {
          processedExchangeRates[curr] = parseFloat(rate);
        } else {
          processedExchangeRates[curr] = null;
        }
      });

      const updatedCurrencyData = {
        exchangeRates: processedExchangeRates,
        updatedBy: user.uid,
      };

      const response = await api.put(`${API_BASE_URL}/currency/${currency._id}`, updatedCurrencyData);

      if (response.status === 200) {
        toast.success("Currency updated successfully!");
        onUpdate(); // Refresh the currency list
        setShowModal(false); // Close modal
      } else {
        toast.error("Failed to update currency.");
      }
    } catch (error) {
      console.error("Error updating currency:", error);
      toast.error("Server error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderExchangeRateInputs = () => {
    if (!currencyData.name) return null;

    return currencyOptions
      .filter(option => option.name !== currencyData.name)
      .map(option => (
        <div key={option.name}>
          <label className="block font-medium">
            Exchange Rate to {option.name} ({option.symbol}):
          </label>
          <input
            type="number"
            step="any"
            value={currencyData.exchangeRates[option.name]}
            onChange={(e) => handleExchangeRateChange(option.name, e.target.value)}
            placeholder={`1 ${currencyData.name} = ? ${option.name}`}
            className="w-full p-2 border rounded-md"
          />
        </div>
      ));
  };

  return (
    showModal && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        onClick={() => setShowModal(false)}
      >
        <div
          className="relative bg-white p-6 rounded-lg shadow-lg w-96 z-50 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setShowModal(false)}
            className="absolute top-3 right-7 text-gray-500 text-2xl hover:text-red-500"
          >
            &times;
          </button>

          <h2 className="text-lg font-semibold mb-4">Edit Currency Exchange Rates</h2>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block font-medium">Base Currency:</label>
              <input
                type="text"
                value={`${currencyData.name} (${currencyData.symbol})`}
                readOnly
                className="w-full p-2 border rounded-md bg-gray-100"
              />
            </div>

            {renderExchangeRateInputs()}

            {currencyData.name && (
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> You are updating exchange rates FROM {currencyData.name} TO other currencies.
                  Leave fields empty if you don't want to set that exchange rate.
                </p>
              </div>
            )}

            <div className="flex justify-start gap-2 mt-4">
              <button
                type="submit"
                className="px-4 py-2 text-blue-600 border-blue-600 btnBg flex rounded-[5px]"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-300 rounded-md"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

export default CurrencyEditModal;